// Core
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  Image,
  Text,
  Flex,
  useColorModeValue,
  useDisclosure
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { hexToRgba, drawCircleOnMap } from 'services/Utils';
import PartnerModal from '../../PartnerModal';
import placeholder from 'assets/img/placeholder-categories.png';
import placeholderPartner from 'assets/img/placeholder-partner.jpg';

// Components
import { CloseClusterIcon, MapMeIcon } from 'components/Icons/Icons';

export default function MapWithPoints({
  userData,
  search,
  categoryId,
  tagIds,
  locations,
  isPopular,
  isFeatured,
  isShop,
  isReady,
  userCoords,
  discountsRange,
  nearMe,
  setFilterResults
}) {
  const [rangeKm, setRangeKm] = useState(20);
  const [isLoadingCluster, setIsLoadingCluster] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(10); 
  const [move, onMove] = useState({ lat: -37.81826557442427, long: 144.95669917457028 });
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const limitCluster = 24;
  const [offsetCluster, setOffsetCluster] = useState(0);
  const [dtoCluster, setDtoCluster] = useState();
  const [clusterData, setClusterData] = useState([]);
  const [clusterDataMeta, setClusterDataMeta] = useState();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [activeItem, setActiveItem] = useState({});
  const mapTheme = useColorModeValue('mapbox://styles/mapbox/light-v11', 'mapbox://styles/mapbox/dark-v11');
  const mapGetMeBg = useColorModeValue('rgb(251 251 251 / 80%)','rgba(14, 22, 51, 0.80)');  
  const boxClusterBg = useColorModeValue('rgb(251 251 251 / 70%)','rgba(14, 22, 51, 0.70)');
  const boxClusterIconColor = useColorModeValue('#0E1633','#9FABCB');    

  const showItem = async (item) => {
    setActiveItem(item);
    onOpen();
    if (userData) {
      try { await apiService.clickPartner({ id: item?.id }); } catch (e) {}
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: [move, search, discountsRange, categoryId, tagIds, locations, isFeatured, isPopular, isShop],
    queryFn: async () => {
      let params = { ...move, range: rangeKm * 200 };
      if (search) { params.search = search; }
      if (categoryId) { params.categoryIds = categoryId; }
      if (tagIds) { params.tagIds = tagIds; }
      if (locations) { params.locations = locations; }
      if (isPopular) { params.sort = isPopular; }
      if (isFeatured) { params.tops = isFeatured; }
      if (isShop) { params.tops = isShop; }
      if (discountsRange) { params = {...params, discount: discountsRange }; }

      const response = await apiService.getPartnersMaps(params);
      const totalCount = Array.isArray(response?.data) && response.data.length > 0
        ? response.data.reduce((sum, item) => sum + (item.count || 0), 0)
        : 0;

      setFilterResults(totalCount);
      return response.data;
    },
    refetchOnWindowFocus: false,
    enabled: isReady,
    retry: false
  });

  const showCluster = async (dto) => {
    setIsLoadingCluster(true);
    setDtoCluster(dto);

    let params = { 
      range: dto.range, 
      lat: dto.lat, 
      long: dto.long,
      limit: dto.limit,
      offset: dto.offset
    };

    if (search) { params.search = search; }
    if (categoryId) { params.categoryIds = categoryId; }
    if (dto.categoryIds) { params.categoryIds = [...dto.categoryIds, categoryId]; }
    if (tagIds) { params.tagIds = tagIds; }
    if (locations) { params.locations = locations; }
    if (isPopular) { params.sort = isPopular; }
    if (isFeatured) { params.tops = isFeatured; }
    if (isShop) { params.tops = isShop; }
    if (discountsRange) { params = {...params, discount: discountsRange }; }

    /*const response = await apiService.getPartnersMapsCluster(params);
    setIsLoadingCluster(false);
    setClusterData(response.data);*/

    try {
      const response = await apiService.getPartnersMapsCluster(params);
      setClusterData((prevData) => [...prevData, ...response.data]);
      setClusterDataMeta(response.meta);
    } catch (error) {
      setIsLoadingCluster(false);
    } finally {
      setIsLoadingCluster(false);
    }
  };

  const getMe = async () => {
    console.log(userCoords || null);

    if (mapRef.current && userCoords.lat && userCoords.long) {
      mapRef.current.flyTo({
        center: [userCoords.long, userCoords.lat],
        zoom: 14,       
        speed: 1.2,  
        curve: 1.42,    
        essential: true, 
      });
    }
  };

  useEffect(() => {
    if (!window.mapboxgl || mapRef.current) { return; }

    const container = document.getElementById('map-multy');
    const width = container.offsetWidth;
    const height = container.offsetHeight;
    const screenSizeInPixels = Math.min(width, height);

    function radiusKmToZoom(radiusKm, latitude) {
      const earthCircumferenceKm = 40075;
      const latRad = (latitude * Math.PI) / 180;
      const metersPerPixel = (radiusKm * 500) / screenSizeInPixels;
      const mapWidthInMeters = earthCircumferenceKm * 1000 * Math.cos(latRad);
      const mapWidthInPixels = mapWidthInMeters / metersPerPixel;
      return Math.log2(mapWidthInPixels / 256);
    }

    const zoom = radiusKmToZoom(rangeKm, move.lat);
    setZoomLevel(zoom);

    window.mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const map = new window.mapboxgl.Map({
      container: 'map-multy',
      style: mapTheme,
      center: [move.long, move.lat],
      zoom
    });

    mapRef.current = map;

    map.on('zoomend', () => {
      const newZoom = map.getZoom();
      const metersPerPixel = 40075000 * Math.cos(move.lat * Math.PI / 180) / (Math.pow(2, newZoom) * 256);
      const approximateRangeKm = (metersPerPixel * 600) / 1000;
      setRangeKm(parseFloat(approximateRangeKm.toFixed(0)));
    });

    map.on('moveend', () => {
      const center = map.getCenter();
      onMove({ lat: parseFloat(center.lat.toFixed(6)), long: parseFloat(center.lng.toFixed(6)) });
    });

    return () => map.remove();
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setStyle(mapTheme);
    }
  }, [mapTheme]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userCoords) { return; }

    const onStyleLoad = () => {
      if (userCoords?.lat !== 0) {
        drawCircleOnMap(map, userCoords, 2);
      }
    };

    map.on('style.load', onStyleLoad);

    if (map.isStyleLoaded()) {
      onStyleLoad();
    }

    return () => {
      map.off('style.load', onStyleLoad);
    };
  }, [mapTheme, userCoords]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !Array.isArray(data)) { return; }

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    data.forEach((point) => {
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.transition = 'none';

      if (point.count > 1 && point?.categories[0]?.image) {
        el.style.backgroundColor = point?.categories[0]?.color || '#1E7735';
        el.style.position = 'relative';
        el.style.boxShadow = `0px 0px 0px 3px ${hexToRgba(point?.categories[0]?.color || '#1E7735', 0.7)}`;

        const label = document.createElement('div');
        label.textContent = point.count;
        label.style.position = 'absolute';
        label.style.top = '71%';
        label.style.left = '50%';
        label.style.transform = 'translate(-50%, -50%)';
        label.style.color = 'white';
        label.style.fontWeight = 'bold';
        label.style.fontSize = '17px';
        el.appendChild(label);

        const icon = document.createElement('img');
        icon.src = point.categories[0].image;
        icon.style.position = 'absolute';
        icon.style.top = '6px';
        icon.style.left = '50%';
        icon.style.transform = 'translateX(-50%)';
        icon.style.width = '24px';
        icon.style.height = '24px';
        icon.style.filter = 'brightness(0) invert(1)';
        icon.style.pointerEvents = 'none';
        el.appendChild(icon);
      } else if (point.count > 1 && !point?.categories[0]?.image) {
        el.style.backgroundImage = `url(${placeholder})`;
        el.style.backgroundSize = '44%';
        el.style.backgroundColor = point?.categories[0]?.color || '#1E7735';
        el.style.backgroundPosition = '50% 4px';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.boxShadow = `0px 0px 0px 3px ${hexToRgba('#1E7735', 0.7)}`;
        el.textContent = point.count;
      } else if (point.count === 1) {
        const iconHtml = `
          <img src="${point?.data[0]?.image}" 
            style="background-size: cover; width: 51px; height: 51px; 
            position: absolute; border-radius: 50px;
            margin-top: -20px;
            background-position: center center; background-repeat: no-repeat">
          <svg width="64" height="84" viewBox="0 0 64 84" fill=${point?.categories[0]?.color || '#1E7735'}>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M32 0C49.6731 0 64 14.3269 64 32C64 34.1572 63.7865 36.2646 63.3796 38.3021C60.5552 59.5859 32.1682 83.8095 32.1682 83.8095C32.1682 83.8095 8.01051 63.195 2.04885 43.2909C0.724581 39.7797 0 35.9745 0 32C0 14.3269 14.3269 0 32 0Z" 
            stroke-width="0px"
            stroke="#FFF"
            fill=${point?.categories[0]?.color || '#1E7735'}/>
          </svg>
        `;

        el.innerHTML = iconHtml;
      }

      if (point.count > 1) {
        el.style.color = 'white';
        el.style.fontWeight = 'bold';
        el.style.paddingTop = '22px';
        el.style.fontSize = '17px';
        el.style.width = '57px';
        el.style.height = '57px';
      }

      el.style.borderRadius = '50%';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';

      if (point.count > 1) {
        el.style.cursor = 'pointer';
        el.onclick = () => {
          showCluster({...point?.clusterPoint, limit: limitCluster, offset: offsetCluster, categoryIds: (point?.categories ?? []).map(item => item.id)});
        };
      } else if (point.count === 1) {
        el.style.cursor = 'pointer';
        el.onclick = () => {
          showItem(point?.data[0]);
        };
      }

      const marker = new window.mapboxgl.Marker(el).setLngLat([point.long, point.lat]).addTo(map);
      markersRef.current.push(marker);
    });

    if (!map.isStyleLoaded()) {
      map.once('style.load', () => {
        if (userCoords?.lat !== 0) {
          drawCircleOnMap(map, userCoords, 2);
        }
      });
    } else {
      if (userCoords?.lat !== 0) {
        drawCircleOnMap(map, userCoords, 2);
      }
    }

  }, [data, userCoords]);

  const handleScrollCluster = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight && !isLoading) {
      if(clusterData?.length === clusterDataMeta?.total){
        return;
      }

      setOffsetCluster(offsetCluster + limitCluster);
      showCluster({...dtoCluster, offset: offsetCluster + limitCluster, limit: limitCluster});
    }
  };

  return (
    <Box position="relative">
      <div id="map-multy" style={{ height: 'calc(100vh - 250px)', width: '100%', borderRadius: '16px' }} />

      <Box display={clusterData.length > 0 ? 'block' : 'none' } 
        maxW="372px" w="100%" p="16px 16px 0px 16px" position="absolute" 
        top="0px" left="0px" 
        height="calc(100vh - 250px)" 
        onScroll={handleScrollCluster} 
        overflow="scroll">
        <Box bg={boxClusterBg} 
          backdropFilter="blur(6px)" 
          borderRadius="16px" 
          mb="16px" 
          p="16px 16px 0 16px" 
          position="relative" 
          overflow="scroll">
          <Flex w="100%" justifyContent="space-between" alignItems="center" mb="18px">
            <Text variant="title-md" fontStyle="italic" fontWeight="900">Explore retail</Text>
            <CloseClusterIcon 
              onClick={() => { 
                setClusterData([]); 
                setDtoCluster(); 
                setClusterDataMeta(); 
                setOffsetCluster(0);
              }}
              cursor="pointer"
              fontSize="31px" color={boxClusterIconColor} />
          </Flex>

          { clusterData?.map((item, index) => (
            <Flex cursor="pointer" key={index} gap="12px" alignItems="center" mb="16px" onClick={() => showItem(item)}>
              <Image src={item?.image ?? placeholderPartner} 
                alt="partner" 
                objectFit="cover"
                borderRadius="12px" 
                maxW="118px" w="100%"
                maxH="80px" h="100%" />
              <Text variant="title-md" fontWeight="900" fontStyle="italic">{item?.name}</Text>
            </Flex>
          ))}
        </Box>
      </Box>

      { userCoords && userCoords?.lat !== 0 && 
        <Box 
          w="56px" 
          h="56px"
          position="absolute"
          bottom="16px"
          right="16px"
          borderRadius="16px"
          background={mapGetMeBg}
          boxShadow="4px 4px 12px 0px rgba(0, 0, 0, 0.30)"
          p="12px"
          cursor="pointer"
          onClick={getMe}
          backdropFilter="blur(6px)"
          >
          <MapMeIcon fontSize="32px" />
        </Box>
      }

      <Button
        variant="ghost-md"
        position="absolute"
        right="5px"
        w="40px"
        top="0px"
        transform="scale(1.2)"
        pointerEvents="none"
        isLoading={isLoading} />

      <Button
        variant="ghost-md"
        position="absolute"
        right="5px"
        w="40px"
        top="0px"
        transform="scale(1.2)"
        pointerEvents="none"
        isLoading={isLoadingCluster} />

      <PartnerModal item={activeItem} userData={userData} isOpen={isOpen} onOpen={onOpen} onClose={onClose} />
    </Box>
  );
}
